import { useState } from "react";
import { Plus, GripVertical, Trash2, Truck } from "lucide-react";
import { toast } from "sonner";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";

const KEY_PATTERN = /^[a-z][a-z0-9_]{0,31}$/;

export default function DeliveryOptionsManager({ options, onChange }) {
    const [newKey, setNewKey] = useState("");
    const [newLabel, setNewLabel] = useState("");
    const [newFee, setNewFee] = useState("10");
    const [newRequiresAddress, setNewRequiresAddress] = useState(true);
    const [draggedKey, setDraggedKey] = useState("");

    const updateOption = (index, patch) => {
        onChange(options.map((option, currentIndex) => (
            currentIndex === index ? { ...option, ...patch } : option
        )));
    };

    const removeOption = (index) => {
        const option = options[index];
        if (!window.confirm(`确定删除「${option.label}」吗？`)) {
            return;
        }
        onChange(options.filter((_, currentIndex) => currentIndex !== index));
    };

    const moveOption = (fromKey, toKey) => {
        if (!fromKey || fromKey === toKey) {
            return;
        }
        const fromIndex = options.findIndex((option) => option.key === fromKey);
        const toIndex = options.findIndex((option) => option.key === toKey);
        if (fromIndex < 0 || toIndex < 0) {
            return;
        }
        const next = [...options];
        const [moved] = next.splice(fromIndex, 1);
        next.splice(toIndex, 0, moved);
        onChange(next);
    };

    const addOption = () => {
        const key = newKey.trim().toLowerCase();
        const label = newLabel.trim();
        const fee = Number(newFee);
        if (!KEY_PATTERN.test(key)) {
            toast.error("key 必须小写英文开头，只含 a-z / 0-9 / _");
            return;
        }
        if (options.some((option) => option.key === key)) {
            toast.error("这个 key 已存在");
            return;
        }
        if (!label) {
            toast.error("请填写顾客看到的配送名称");
            return;
        }
        if (!Number.isFinite(fee) || fee < 0) {
            toast.error("运费必须是 0 或正数");
            return;
        }
        onChange([
            ...options,
            {
                key,
                label,
                fee: Number(fee.toFixed(2)),
                enabled: true,
                requires_address: newRequiresAddress,
            },
        ]);
        setNewKey("");
        setNewLabel("");
        setNewFee("10");
        setNewRequiresAddress(true);
        toast.success(`已新增「${label}」，别忘了保存`);
    };

    return (
        <Card className="space-y-4 border-slate-200 p-6 shadow-sm" data-testid="delivery-options-card">
            <div>
                <h2 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-gold-600">
                    <Truck className="h-4 w-4" /> 配送方式管理
                </h2>
                <p className="mt-1 text-xs text-slate-500">
                    拖动左侧把手调整落地页顺序；关闭后不显示，删除不会影响已有订单记录。
                </p>
            </div>

            <div className="space-y-2" data-testid="delivery-options-list">
                {options.map((option, index) => (
                    <div
                        key={option.key}
                        draggable
                        data-testid={`delivery-option-row-${option.key}`}
                        onDragStart={() => setDraggedKey(option.key)}
                        onDragOver={(event) => event.preventDefault()}
                        onDrop={() => {
                            moveOption(draggedKey, option.key);
                            setDraggedKey("");
                        }}
                        className={`flex flex-wrap items-center gap-2 rounded-md border p-3 transition-colors ${
                            draggedKey === option.key
                                ? "border-gold-500 bg-gold-50"
                                : "border-slate-200 bg-white"
                        }`}
                    >
                        <div
                            className="flex h-8 w-6 cursor-grab items-center justify-center text-gold-500 active:cursor-grabbing"
                            data-testid={`delivery-option-drag-${option.key}`}
                            title="拖动调整顺序"
                        >
                            <GripVertical className="h-4 w-4" />
                        </div>
                        <code className="w-20 shrink-0 rounded border border-gold-200 bg-gold-50 px-2 py-1 text-center text-xs font-bold text-gold-700">
                            {option.key}
                        </code>
                        <Input
                            value={option.label}
                            data-testid={`delivery-option-label-${option.key}`}
                            onChange={(event) => updateOption(index, { label: event.target.value })}
                            className="min-w-[9rem] flex-1"
                            placeholder="顾客看到的名称"
                        />
                        <div className="flex items-center gap-1">
                            <span className="text-xs text-slate-500">RM</span>
                            <Input
                                type="number"
                                min="0"
                                step="0.01"
                                value={option.fee}
                                data-testid={`delivery-option-fee-${option.key}`}
                                onChange={(event) => updateOption(index, {
                                    fee: Math.max(0, Number(event.target.value) || 0),
                                })}
                                className="w-24 font-mono text-sm"
                                aria-label={`${option.label} 运费`}
                            />
                        </div>
                        <div className="flex items-center gap-2 px-1">
                            <Label htmlFor={`delivery-address-${option.key}`} className="text-xs text-slate-600">
                                要地址
                            </Label>
                            <Switch
                                id={`delivery-address-${option.key}`}
                                data-testid={`delivery-option-address-${option.key}`}
                                checked={option.requires_address}
                                onCheckedChange={(value) => updateOption(index, {
                                    requires_address: value,
                                })}
                            />
                        </div>
                        <div className="flex items-center gap-2 px-1">
                            <Label htmlFor={`delivery-enabled-${option.key}`} className="text-xs text-slate-600">
                                显示
                            </Label>
                            <Switch
                                id={`delivery-enabled-${option.key}`}
                                data-testid={`delivery-option-enabled-${option.key}`}
                                checked={option.enabled}
                                onCheckedChange={(value) => updateOption(index, { enabled: value })}
                            />
                        </div>
                        <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            data-testid={`delivery-option-delete-${option.key}`}
                            onClick={() => removeOption(index)}
                            className="text-red-600 hover:bg-red-50"
                            title={`删除 ${option.label}`}
                        >
                            <Trash2 className="h-4 w-4" />
                        </Button>
                    </div>
                ))}
            </div>

            <div className="space-y-3 border-t border-slate-200 pt-4">
                <Label className="text-xs font-semibold uppercase tracking-wider text-gold-600">新增配送方式</Label>
                <div className="flex flex-wrap items-end gap-2">
                    <div className="space-y-1">
                        <Label htmlFor="new-delivery-key" className="text-xs text-slate-500">key</Label>
                        <Input
                            id="new-delivery-key"
                            value={newKey}
                            data-testid="new-delivery-key-input"
                            onChange={(event) => setNewKey(
                                event.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ""),
                            )}
                            placeholder="如 singapore"
                            className="w-36 font-mono"
                            maxLength={32}
                        />
                    </div>
                    <div className="min-w-48 flex-1 space-y-1">
                        <Label htmlFor="new-delivery-label" className="text-xs text-slate-500">顾客名称</Label>
                        <Input
                            id="new-delivery-label"
                            value={newLabel}
                            data-testid="new-delivery-label-input"
                            onChange={(event) => setNewLabel(event.target.value)}
                            placeholder="如 新加坡配送"
                        />
                    </div>
                    <div className="space-y-1">
                        <Label htmlFor="new-delivery-fee" className="text-xs text-slate-500">运费（RM）</Label>
                        <Input
                            id="new-delivery-fee"
                            type="number"
                            min="0"
                            step="0.01"
                            value={newFee}
                            data-testid="new-delivery-fee-input"
                            onChange={(event) => setNewFee(event.target.value)}
                            className="w-28 font-mono"
                        />
                    </div>
                    <div className="flex items-center gap-2 pb-2">
                        <Label htmlFor="new-delivery-address" className="text-xs text-slate-600">要地址</Label>
                        <Switch
                            id="new-delivery-address"
                            data-testid="new-delivery-address-switch"
                            checked={newRequiresAddress}
                            onCheckedChange={setNewRequiresAddress}
                        />
                    </div>
                    <Button
                        type="button"
                        data-testid="add-delivery-option-button"
                        onClick={addOption}
                        className="gap-1.5 bg-gold-600 text-white hover:bg-gold-700"
                    >
                        <Plus className="h-4 w-4" /> 添加
                    </Button>
                </div>
            </div>
        </Card>
    );
}